import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import Box from '@mui/material/Box';
import { Cell } from './Cell';
import { getProperty, getConditionalStyle } from './util';

interface TableCellStyleProps {
  column: {
    truncate: string;
    allowOverflow: boolean;
    style: string;
  };
  extendedCellStyle: string;
  mobileCards: boolean;
}

const mobileCardLayout = css`
  @media (max-width: 960px) {
    padding: 6px 16px;
  }
`;

const TableCellStyle = styled(Cell)`
  font-size: ${(props) => props.theme.rows.fontSize};
  font-weight: 400;

  div:first-child {
    white-space: ${(props: TableCellStyleProps) => (props.column.truncate ? 'nowrap' : 'normal')};
    overflow: ${(props) => (props.column.allowOverflow ? 'visible' : 'hidden')};
    text-overflow: ellipsis;
  }

  ${(props) => props.column.style};
  ${(props) => props.extendedCellStyle};
  ${(props: TableCellStyleProps) => props.mobileCards && mobileCardLayout};
`;

export interface TableCellProps {
  id: string;
  column: any; // TODO
  row: any;
  mobileCards: boolean;
}

const TableCell = memo((props: TableCellProps) => {
  const { id, column, row, mobileCards } = props;
  if (column.omit) {
    return null;
  }

  // apply a tag that TableRow will use to stop event propagation when TableCell is clicked
  const dataTag = column.ignoreRowClick || column.button ? null : '___react-data-table-allow-propagation___';
  const extendedCellStyle = getConditionalStyle(row, column.conditionalCellStyles);

  const content = (
    <>
      {!column.cell && <div data-tag={dataTag}>{getProperty(row, column.selector, column.format)}</div>}
      {column.cell && column.cell(row)}
    </>
  );

  return (
    <TableCellStyle
      id={id}
      role="cell"
      column={column}
      data-tag={dataTag}
      className="rdt_TableCell"
      extendedCellStyle={extendedCellStyle}
      mobileCards={mobileCards}
    >
      {mobileCards ? (
        // Mount `content` once (custom cells may hold state/effects); only the card label toggles by breakpoint.
        // Width stays auto on desktop so column `right`/`center` alignment on the cell still applies.
        <Box display="flex" flexWrap="wrap" justifyContent="space-between" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Box display={{ xs: 'block', md: 'none' }}>{column.name}</Box>
          {content}
        </Box>
      ) : (
        content
      )}
    </TableCellStyle>
  );
});

/*TableCell.propTypes = {
  id: PropTypes.string.isRequired,
  column: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
};*/

export default TableCell;
